import { Avatar, makeStyles } from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import axios from 'axios';
import { differenceInCalendarMonths, endOfDay, startOfDay } from 'date-fns';
import React from 'react';
import { Plus, Trash, Upload } from 'react-feather';
import { Link } from 'react-router-dom';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { Area, AssetType, CostCenter } from '../../../types/asset';
import { enumToStatusType, projectStatus } from '../../../types/common';
import { ProjectSummary } from '../../../types/project';
import { Report } from '../../../types/report';
import { exportToExcel, getContentUrl } from '../../../util/file';
import { Badge, Table } from '../../global';
import { FilterBar } from '../../global/FilterBar';
import { Button } from '../../global/forms/Button';
import { computeMetrics } from '../details/Metrics';
import { DeleteReport } from '../dialogs/DeleteReport';
import { ExportReport } from '../dialogs/ExportReport';
import { ReportEditor } from '../dialogs/ReportEditor';
import reportListNoReports from './ReportListNoReports.svg';

const useReportListClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        background: theme.colors.neutral[6],
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 40,
        background: theme.colors.darkBlue[1],
    },
    labels: {
        display: 'flex',
        flexDirection: 'column',
        '& > span:first-child': {
            color: theme.colors.white,
            ...theme.typography.page.title,
            marginBottom: 0,
        },
        '& > span:last-child': {
            color: theme.colors.neutral[3],
            ...theme.typography.content.label.default,
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        gap: 16,
        padding: '24px 40px',
        '& > span': {
            color: theme.colors.neutral[2],
            ...theme.fonts.h5,
        },
    },
    table: {
        '& a': { textDecoration: 'none' },
    },
});

export const ReportList = () => {
    const classes = useReportListClasses();
    const dispatch = useTypedDispatch();
    const { t } = usePrefixTranslation('report', { report: 'list.ReportList' });
    const { report_list } = useTypedSelector(state => ({ report_list: state.report_list }));
    const [reportEditorOpen, setReportEditorOpen] = React.useState(false);

    React.useEffect(() => {
        dispatch(actions.report_list.get());
    }, []);

    const openCreateReportDialog = () => (dispatch(actions.report.create()), setReportEditorOpen(true));

    const reports = [
        {
            label: 'label-upcoming-in-progress',
            reports: report_list.reports.filter(r => +startOfDay(r.end_date) >= +startOfDay(new Date())),
        },
        {
            label: 'label-completed',
            reports: report_list.reports.filter(r => +startOfDay(r.end_date) < +startOfDay(new Date())),
        },
    ];

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.labels}>
                    <span>{t('title')}</span>
                    <span>{t('subtitle')}</span>
                </div>
                <Button theme='primary' onClick={openCreateReportDialog}>
                    <Plus />
                    {t('button-create')}
                </Button>
            </div>
            <div className={classes.content}>
                <FilterBar
                    widgets={[
                        {
                            type: 'search',
                            label: t('filter-search'),
                            debounce: 300,
                            autocompleteKey: 'report-list',
                            onChange: query => dispatch(actions.report_list.get({ query })),
                        },
                    ]}
                />
                {reports.map(({ label, reports }) => (
                    <React.Fragment key={label}>
                        <span>{t(label, { count: reports.length })}</span>
                        <Table
                            className={classes.table}
                            loading={report_list.fetching ? 5 : 0}
                            columns={[
                                { element: t('table-header-title'), size: 'minmax(250px, 1fr)' },
                                { element: t('table-header-status'), size: '210px' },
                                { element: t('table-header-template'), size: '210px' },
                                { element: t('table-header-last-updated'), size: '210px' },
                                { element: t('table-header-created-by'), size: '210px' },
                                { element: '', size: '56px' },
                            ]}
                            cells={reports.map(report => [
                                <ColumnTitle report={report} />,
                                <ColumnStatus report={report} />,
                                <ColumnTemplate report={report} />,
                                <ColumnLastUpdated report={report} />,
                                <ColumnUser report={report} />,
                                <ColumnActions report={report} />,
                            ])}
                            fixed={1}
                            renderEmpty={<TableEmpty onCreateReport={openCreateReportDialog} />}
                            rowBuilder={row => props => <Link {...(props as any)} to={reports[row].id.toString()} />}
                        />
                    </React.Fragment>
                ))}
            </div>
            <ReportEditor
                mode='create'
                open={reportEditorOpen}
                onCancel={() => setReportEditorOpen(false)}
                onSave={async report => {
                    setReportEditorOpen(false);
                    dispatch(actions.report.create());
                    dispatch(actions.report.edit(report));
                    dispatch(actions.report.commit());
                    await dispatch(actions.report.update());
                    await dispatch(actions.report_list.get());
                }}
            />
        </div>
    );
};

const useTableEmptyClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        color: theme.colors.neutral[2],
        '& > span:first-of-type': { ...theme.fonts.h5 },
        '& > span:last-of-type': { ...theme.fonts.label },
        '& > button': { marginTop: 10 },
    },
});

const TableEmpty = (props: { onCreateReport: () => void }) => {
    const classes = useTableEmptyClasses();
    const { t } = usePrefixTranslation('report', { report: 'list.ReportList' });

    return (
        <div className={classes.root}>
            <img src={reportListNoReports} width={200} height={150} alt='table-empty' />
            <span>{t('table-empty-title')}</span>
            <span>{t('table-empty-subtitle')}</span>
            <Button theme='blue' onClick={props.onCreateReport}>
                <Plus />
                {t('button-create')}
            </Button>
        </div>
    );
};

const useTableColumnsClasses = makeStyles({
    title: {
        color: theme.colors.neutral[1],
        ...theme.fonts.label,
    },
    template: {
        color: theme.colors.neutral[2],
        ...theme.fonts.label,
    },
    lastUpdated: {
        color: theme.colors.neutral[2],
        ...theme.fonts.label,
    },
    createdBy: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        color: theme.colors.neutral[2],
        ...theme.fonts.label,
        '& > div': { width: 16, height: 16 },
    },
    actions: {
        padding: 10,
        margin: -5,
    },
    actionButton: {
        justifyContent: 'flex-start',
    },
});

const ColumnTitle = (props: { report: Report }) => {
    const classes = useTableColumnsClasses();
    return <span className={classes.title}>{props.report.title}</span>;
};

const ColumnStatus = (props: { report: Report }) => {
    const { t } = usePrefixTranslation('common', { common: 'status' });
    const status =
        +startOfDay(props.report.start_date) > +startOfDay(new Date())
            ? 'not started'
            : +startOfDay(props.report.end_date) < +startOfDay(new Date())
            ? 'completed'
            : 'in progress';
    return <Badge type={enumToStatusType[`report:${status}`]}>{t(status)}</Badge>;
};

const ColumnTemplate = (props: { report: Report }) => {
    const classes = useTableColumnsClasses();
    const { t } = usePrefixTranslation('report', { report: 'list.ReportList' });
    const months = differenceInCalendarMonths(props.report.end_date, props.report.start_date);
    const template = months <= 1 ? 'monthly' : months <= 3 ? 'quarterly' : 'yearly';
    return <span className={classes.template}>{t(`template-${template}`)}</span>;
};

const ColumnLastUpdated = (props: { report: Report }) => {
    const classes = useTableColumnsClasses();
    const { t } = usePrefixTranslation('report', { report: 'list.ReportList' });
    return <span className={classes.template}>{t('last-updated-date', { date: new Date() })}</span>;
};

const ColumnUser = (props: { report: Report }) => {
    const classes = useTableColumnsClasses();
    const dispatch = useTypedDispatch();
    const { users } = useTypedSelector(state => ({ users: state.user.users }));
    const user = users.find(user => user.id === props.report.user);
    const [picture, setPicture] = React.useState('');

    React.useEffect(() => {
        dispatch(actions.user.get());
    }, []);

    React.useEffect(() => {
        if (!user || user.picture === '') return setPicture('');
        getContentUrl(user.picture).then(url => setPicture(url));
    }, [user?.picture]);

    return (
        <div className={classes.createdBy}>
            {!!user && user.picture !== '' && <Avatar src={picture} />}
            <span>{user?.full_name}</span>
        </div>
    );
};

const ColumnActions = (props: { report: Report }) => {
    const classes = useTableColumnsClasses();
    const { t } = usePrefixTranslation('report', { report: 'list.ReportList' });
    const dispatch = useTypedDispatch();
    const { report } = useTypedSelector(store => ({ report: store.report }));
    const [exportReportOpen, setExportReportOpen] = React.useState(false);
    const [deleteReportOpen, setDeleteReportOpen] = React.useState(false);

    return (
        <>
            <Button
                className={classes.actions}
                theme='lighter'
                icon='rounded'
                onClick={e => e.preventDefault()}
                menu={
                    <>
                        <Button
                            className={classes.actionButton}
                            theme='blue'
                            onClick={() => (setExportReportOpen(true), dispatch(actions.report.get(props.report.id)))}
                        >
                            <Upload />
                            {t('button-export')}
                        </Button>
                        <Button
                            className={classes.actionButton}
                            theme='warning'
                            onClick={() => (setDeleteReportOpen(true), dispatch(actions.report.get(props.report.id)))}
                        >
                            <Trash />
                            {t('button-delete')}
                        </Button>
                    </>
                }
            >
                <MoreVert />
                <div style={{ display: 'contents' }} onClick={e => e.stopPropagation()}>
                    <ExportReport
                        loading={report.fetching || report.status === 'updating'}
                        title={report.report?.title ?? ''}
                        open={exportReportOpen}
                        onCancel={() => {
                            setExportReportOpen(false);
                            dispatch(actions.report.reset());
                        }}
                        onExport={(format, fileName) => {
                            setExportReportOpen(false);
                            if (format === 'pdf') alert('pdf exports are only enabled within the report details page');
                            else if (format === 'excel') {
                                const typesPromise = axios.get<AssetType[]>('/asset-types');
                                const areasPromise = axios.get<Area[]>('/areas');
                                const costCentersPromise = axios.get<CostCenter[]>('/cost-centers');
                                Promise.all([typesPromise, areasPromise, costCentersPromise]).then(responses => {
                                    const types = responses[0].data;
                                    const areas = responses[1].data;
                                    const costCenters = responses[2].data;
                                    if (!report.report || !types || !areas || !costCenters) return;
                                    const selectedTypes =
                                        report.report.asset_type_filters.length > 0
                                            ? new Set(
                                                  types
                                                      .filter(t => report.report?.asset_type_filters.includes(t.id))
                                                      .map(t => t.name),
                                              )
                                            : undefined;
                                    const selectedAreas =
                                        report.report.area_filters.length > 0
                                            ? new Set(
                                                  areas
                                                      .filter(a => report.report?.area_filters.includes(a.id))
                                                      .map(a => a.name),
                                              )
                                            : undefined;
                                    const selectedCostCenters =
                                        report.report.cost_center_filters.length > 0
                                            ? new Set(
                                                  costCenters
                                                      .filter(c => report.report?.cost_center_filters.includes(c.id))
                                                      .map(c => c.name),
                                              )
                                            : undefined;
                                    const forecastedPromise = axios.get<ProjectSummary[]>('/inspections/overview', {
                                        params: {
                                            pagination: 'False',
                                            start_date: startOfDay(report.report!.start_date),
                                            end_date: endOfDay(report.report!.end_date),
                                            inspection_plan__isnull: 'False',
                                        },
                                    });
                                    const scheduledPromise = axios.get<ProjectSummary[]>('/inspections/overview', {
                                        params: {
                                            pagination: 'False',
                                            start_date: startOfDay(report.report!.start_date),
                                            end_date: endOfDay(report.report!.end_date),
                                            status: projectStatus.join(','),
                                        },
                                    });
                                    Promise.all([forecastedPromise, scheduledPromise]).then(responses => {
                                        const forecastedProjects = responses[0].data.filter(
                                            p =>
                                                (!selectedTypes || selectedTypes.has(p.asset_type!)) &&
                                                !selectedAreas &&
                                                (!selectedCostCenters || selectedCostCenters.has(p.cost_center_name!)),
                                        );
                                        const scheduledProjects = responses[1].data.filter(
                                            p =>
                                                (!selectedTypes || selectedTypes.has(p.asset_type!)) &&
                                                !selectedAreas &&
                                                (!selectedCostCenters || selectedCostCenters.has(p.cost_center_name!)),
                                        );
                                        const metrics = computeMetrics(
                                            report.report!,
                                            forecastedProjects,
                                            scheduledProjects,
                                            {
                                                start: report.report?.start_date!,
                                                end: report.report?.end_date!,
                                            },
                                            'month',
                                        );
                                        const forecastedSheetData = metrics.forecasted;
                                        const scheduledSheetData = metrics.scheduled;
                                        const {
                                            scheduled: scheduledData,
                                            forecasted: forecastedData,
                                            ...restData
                                        } = metrics;
                                        exportToExcel(
                                            [
                                                { name: 'Forecasted Projects', data: forecastedSheetData },
                                                { name: 'Scheduled Projects', data: scheduledSheetData },
                                                { name: 'Rest', data: [restData] },
                                            ],
                                            fileName,
                                        );
                                    });
                                });
                            }
                        }}
                    />
                    <DeleteReport
                        loading={report.fetching || report.status === 'updating'}
                        title={report.report?.title ?? ''}
                        open={deleteReportOpen}
                        onCancel={() => {
                            setDeleteReportOpen(false);
                            dispatch(actions.report.reset());
                        }}
                        onDelete={async () => {
                            await dispatch(actions.report.delete());
                            setDeleteReportOpen(false);
                            await dispatch(actions.report_list.get());
                        }}
                    />
                </div>
            </Button>
        </>
    );
};
